import { useState, useRef } from 'react';
import useNotification from './useNotification';
import { getSectionKeywords } from '../constants/sectionKeywords';

/**
 * Custom hook for handling improvement functionality
 */
const useImprove = (projectId) => {
  const [isImproving, setIsImproving] = useState(false);
  const [improvedContent, setImprovedContent] = useState('');
  const [showImprovementModal, setShowImprovementModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [showImproveInput, setShowImproveInput] = useState(null); // Track which section shows input
  const [improvePrompt, setImprovePrompt] = useState('');
  const [isSpecificSectionImprovement, setIsSpecificSectionImprovement] = useState(false);
  const [specificSectionName, setSpecificSectionName] = useState(null);
  const subsectionHeaderRef = useRef(null); // Use ref for synchronous updates
  const sectionRefs = useRef({});
  
  const { showNotification } = useNotification();

  const requestImprovement = async (section, prompt = '') => {
    try {

      console.log('section', section);
      setIsImproving(true);
      setShowImprovementModal(true);
      setImprovedContent('');
      subsectionHeaderRef.current = null; // Reset subsection header for new improvement

      // Verify section ID and version (placeholder for actual implementation)
      if (!section.id || section.id === undefined) {
        throw new Error('Section ID is required');
      }

      console.log('Starting improvement for section:', section.title, 'with prompt:', prompt);

      // Prepare the content that needs to be improved
      let contentToImprove = section.rawContent || section.content || section.code || '';
      
      // Remove title line if present (## F001: Title format)
      contentToImprove = contentToImprove.replace(/^(?:###?|##)\s+F\d+:[^\n]*\n+/, '');

      // Check if the user wants to improve a specific section or text fragment
      let specificSection = null;
      let specificTextFragment = null;
      let sectionContent = contentToImprove;
      console.log('prompt', prompt, contentToImprove); 
      if (prompt) {
         const sectionKeywords = getSectionKeywords(section.type || 'feature');
        const lowerPrompt = prompt.toLowerCase();

        // Try exact section match first
        for (const keyword of sectionKeywords) {
          if (lowerPrompt.includes(keyword)) {
            specificSection = keyword;
            setIsSpecificSectionImprovement(true);
            setSpecificSectionName(keyword);
            console.log('✅ Matched section keyword:', keyword);
            break;
          }
        }

        // First, check if the prompt itself (or part of it) is a text fragment from the content
        let potentialFragment = prompt.trim();
        
        // Remove common prefixes that users might add
        const commonPrefixes = [
          /^(update|improve|rewrite|fix|change|edit|modify)\s*[-:\s]+/i,
          /^improvise\s+this\s+line\s*:\s*/i,
          /^improvise\s+/i
        ];
        
        let cleanedPrompt = potentialFragment;
        for (const prefix of commonPrefixes) {
          cleanedPrompt = cleanedPrompt.replace(prefix, '').trim();
        }
        
        // Try to find the text in content - first try cleaned version, then original
        let foundFragment = null;
        if (cleanedPrompt && contentToImprove.includes(cleanedPrompt)) {
          foundFragment = cleanedPrompt;
        } else if (potentialFragment && contentToImprove.includes(potentialFragment)) {
          foundFragment = potentialFragment;
        } else {
          // Check for quoted text
          const quotedTextMatch = prompt.match(/["']([^"']+)["']/);
          if (quotedTextMatch && contentToImprove.includes(quotedTextMatch[1])) {
            foundFragment = quotedTextMatch[1];
          }
        }

        // If no section match, THEN check for text fragments
        if (!specificSection) {
          // Check if the prompt itself (or part of it) is a text fragment from the content
          let potentialFragment = prompt.trim();

          // INTEGRATION-SPECIFIC: Enhanced text fragment detection
          if (section.type === 'integration') {
            // Remove common prefixes for integrations
            const integrationPrefixes = [
              /^improve\s+this\s+/i,
              /^(update|improve|rewrite|fix|change|edit|modify)\s+/i,
              /^improvise\s+this\s+line\s*:\s*/i,
              /^improvise\s+/i
            ];

            let cleanedPrompt = potentialFragment;
            for (const prefix of integrationPrefixes) {
              cleanedPrompt = cleanedPrompt.replace(prefix, '').trim();
            }

            console.log('🔍 [INTEGRATION] Text Fragment Detection:');
            console.log('  Original prompt:', potentialFragment);
            console.log('  Cleaned prompt:', cleanedPrompt);

            // Try to find the text in content
            let foundFragment = null;
            if (cleanedPrompt && contentToImprove.includes(cleanedPrompt)) {
              foundFragment = cleanedPrompt;
              console.log('  ✅ Found cleaned prompt in content');
            } else if (potentialFragment && contentToImprove.includes(potentialFragment)) {
              foundFragment = potentialFragment;
              console.log('  ✅ Found original prompt in content');
            } else {
              // Try to find partial matches (at least 20 characters)
              if (cleanedPrompt.length >= 20) {
                const words = cleanedPrompt.split(/\s+/);
                if (words.length >= 5) {
                  // Try progressively shorter substrings from the start
                  for (let i = 0; i <= 3; i++) {
                    const substring = words.slice(i).join(' ');
                    if (substring.length >= 20 && contentToImprove.includes(substring)) {
                      foundFragment = substring;
                      console.log('  ✅ Found substantial substring:', substring.substring(0, 50) + '...');
                      break;
                    }
                  }
                }
              }

              // Only check quoted text as last resort for substantial matches
              if (!foundFragment) {
                const quotedTextMatch = prompt.match(/["']([^"']{20,})["']/);
                if (quotedTextMatch && contentToImprove.includes(quotedTextMatch[1])) {
                  foundFragment = quotedTextMatch[1];
                  console.log('  ⚠️ Falling back to quoted text:', quotedTextMatch[1].substring(0, 50) + '...');
                }
              }
            }

            if (foundFragment) {
              specificTextFragment = foundFragment;
              sectionContent = foundFragment;
              setIsSpecificSectionImprovement(true);
              setSpecificSectionName('text fragment');
              console.log('  ✅ Detected text fragment for integration');
              setCurrentSection({
                ...section,
                originalSectionContent: foundFragment
              });
            }
          } else {
            // OTHER TILES: Keep original logic unchanged
            const commonPrefixes = [
              /^(update|improve|rewrite|fix|change|edit|modify)\s*[-:\s]+/i,
              /^improvise\s+this\s+line\s*:\s*/i,
              /^improvise\s+/i
            ];

            let cleanedPrompt = potentialFragment;
            for (const prefix of commonPrefixes) {
              cleanedPrompt = cleanedPrompt.replace(prefix, '').trim();
            }

            // Try to find the text in content - first try cleaned version, then original
            let foundFragment = null;
            if (cleanedPrompt && contentToImprove.includes(cleanedPrompt)) {
              foundFragment = cleanedPrompt;
            } else if (potentialFragment && contentToImprove.includes(potentialFragment)) {
              foundFragment = potentialFragment;
            } else {
              // Check for quoted text
              const quotedTextMatch = prompt.match(/["']([^"']+)["']/);
              if (quotedTextMatch && contentToImprove.includes(quotedTextMatch[1])) {
                foundFragment = quotedTextMatch[1];
              }
            }

            if (foundFragment) {
              // Found a text fragment in the content
              specificTextFragment = foundFragment;
              sectionContent = foundFragment;
              setIsSpecificSectionImprovement(true);
              setSpecificSectionName('text fragment');
              console.log('Detected text fragment:', foundFragment);
              setCurrentSection({
                ...section,
                originalSectionContent: foundFragment
              });
            }
          }
        }

        // If still no match, try fuzzy matching for subsections (integrations only)
        if (!specificSection && !specificTextFragment && section.type === 'integration') {
          // Check if prompt mentions a subsection pattern like "approach 1", "option 2", etc.
          const subsectionPattern = /(?:approa?ch?|option?|approach?|aproch?|aproach?)\s*(\d+)/i;
          const subsectionMatch = lowerPrompt.match(subsectionPattern);

          if (subsectionMatch) {
            const number = subsectionMatch[1];
            // Find matching subsection in content by looking for "### Approach N:" or "### Option N:"
            const content = section.rawContent || section.content || '';
            const headerPattern = new RegExp(`###\\s+(Approach|Option)\\s+${number}:[^\\n]*`, 'i');
            const headerMatch = content.match(headerPattern);

            if (headerMatch) {
              // Found the subsection, use the correct name
              const subsectionType = headerMatch[1].toLowerCase();
              specificSection = `${subsectionType} ${number}`;
              setIsSpecificSectionImprovement(true);
              setSpecificSectionName(specificSection);
              console.log('✅ Fuzzy matched subsection:', specificSection);
            }
          }
        }

        // If no specific section or fragment found, it's a full improvement
        if (!specificSection && !specificTextFragment) {
          setIsSpecificSectionImprovement(false);
          setSpecificSectionName(null);
        }
        
        // Extract the specific section content if requested
        if (specificSection) {
          // Check if it's an integration type AND a subsection (Approach 1, Option 1, etc.)
          const isIntegrationSubsection = section.type === 'integration' && /^(approach|option)\s+\d+/i.test(specificSection);

          if (isIntegrationSubsection) {
            // Handle markdown subsection format (### Approach 1:) - ONLY for integrations
            const escapedSection = specificSection.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

            console.log('🔍 [EXTRACT] Looking for subsection:', specificSection);
            console.log('🔍 [EXTRACT] Escaped:', escapedSection);

            // Extract the full subsection INCLUDING header
            const subsectionRegex = new RegExp(
              `(###\\s+${escapedSection}:[^\n]*)\n([\\s\\S]*?)(?=\\n###\\s+|\\n##\\s+|$)`,
              'i'
            );
            console.log('🔍 [EXTRACT] Regex:', subsectionRegex);

            const match = contentToImprove.match(subsectionRegex);
            console.log('🔍 [EXTRACT] Match found?', !!match);

            if (match) {
              const header = match[1]; // ### Approach 1: Name
              const bodyContent = match[2].trim(); // Content without header

              console.log('✅ [EXTRACT] Header:', header);
              console.log('✅ [EXTRACT] Body (first 500 chars):', bodyContent.substring(0, 500));

              // CRITICAL: Only send the body content for improvement (preserve header)
              sectionContent = bodyContent;

              // Store header in ref for immediate synchronous access
              subsectionHeaderRef.current = header;
              console.log('✅ [EXTRACT] Stored in ref:', subsectionHeaderRef.current);

              // Store the ORIGINAL SUBSECTION content for comparison modal
              setCurrentSection({
                ...section,
                originalSectionContent: bodyContent, // ONLY the subsection body, not full integration
                subsectionHeader: header, // Also store for modal display
                content: bodyContent, // Override section.content to prevent showing full integration
                rawContent: bodyContent // Override rawContent too
              });
            } else {
              console.warn('⚠️ [EXTRACT] No match found! Using full content');
            }
          } else if (section.type === 'integration') {
            // For integration main sections (##), extract the full section content
            // Use flexible regex to match section names that contain the keyword
            // e.g., "endpoints" matches both "## Endpoints" and "## API Endpoints"
            const escapedSection = specificSection.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const mainSectionRegex = new RegExp(
              `(##\\s+.*?${escapedSection}.*?[\\s\\S]*?)(?=\\n##\\s+|$)`,
              'i'
            );
            console.log('🔍 [EXTRACT] Looking for main section containing:', specificSection);
            console.log('🔍 [EXTRACT] Main section regex:', mainSectionRegex);

            const match = contentToImprove.match(mainSectionRegex);
            console.log('🔍 [EXTRACT] Main section match found?', !!match);

            if (match) {
              sectionContent = match[1].trim();
              console.log('✅ [EXTRACT] Integration main section content (first 300 chars):', sectionContent.substring(0, 300));
              setCurrentSection({
                ...section,
                originalSectionContent: sectionContent,
                content: sectionContent,
                rawContent: sectionContent
              });
            } else {
              console.warn('⚠️ [EXTRACT] Main section not found for:', specificSection);
            }
          } else {
            // Handle bold format (**keyword:**) - for ALL other tiles (features, user stories, etc.)
            const sectionRegex = new RegExp(`\\*\\*${specificSection}:?\\*\\*[\\s:]*((?:[^\\*]|\\*(?!\\*)|\\n)+?)(?=\\n\\*\\*|$)`, 'i');
            const match = contentToImprove.match(sectionRegex);
            if (match) {
              sectionContent = match[1].trim();
              // Store original content for modal display
              setCurrentSection({
                ...section,
                originalSectionContent: `**${specificSection.charAt(0).toUpperCase() + specificSection.slice(1)}:** ${sectionContent}`
              });
            }
          }
        } else if (!specificTextFragment) {
          // For full improvement, use the entire content (only if not a text fragment)
          setCurrentSection(section);
        }
      } else {
        // No prompt, full improvement
        setIsSpecificSectionImprovement(false);
        setSpecificSectionName(null);
        setCurrentSection(section);
      }
      
      // Call the improvement API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/projects/${projectId}/improve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            content: specificSection || specificTextFragment ? sectionContent : contentToImprove,
            fullContent: contentToImprove, // Send full content for context
            specificSection: specificSection, // Indicate which section to improve
            specificTextFragment: specificTextFragment, // Indicate specific text fragment to improve
            sectionType: section.type || 'feature',
            sectionId: section.id,
            sectionTitle: section.title,
            userPrompt: prompt,
            preserveStructure: true // Ensure original styling/structure is preserved
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Improve API error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Failed to generate improvement: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Handle agentic actions (remove, edit_field)
      if (result.action === 'remove') {
        // Section should be removed from the artifact
        setImprovedContent(`[REMOVE] ${result.reason || 'Removed by request'}`);
        setCurrentSection({
          ...section,
          action: 'remove',
          reason: result.reason,
        });
        setShowImprovementModal(true);
        return;
      }

      // Ensure we have a string content
      let improvedText = result.improvedContent || result.content || '';
      if (typeof improvedText !== 'string') {
        console.error('Invalid content type received:', typeof improvedText, improvedText);
        throw new Error('Invalid response format from improvement API');
      }

      // Handle different improvement scenarios
      if (specificTextFragment && contentToImprove) {
        // Text fragment improvement - show the fragment and merge it back
        console.log('Handling text fragment improvement, fragment:', specificTextFragment);
        setImprovedContent(improvedText);
        
        // Replace the specific text fragment in the full content
        const mergedContent = contentToImprove.replace(specificTextFragment, improvedText);
        
        setCurrentSection(prev => {
          console.log('Setting currentSection for text fragment, originalSectionContent:', specificTextFragment);
          return {
            ...prev,
            originalSectionContent: specificTextFragment,
            mergedContent: mergedContent,
            isSpecificSection: true
          };
        });
      } else if (specificSection && contentToImprove) {
        // Check if it's an integration subsection
        const isIntegrationSubsection = (currentSection?.type || section.type) === 'integration' &&
                                        /^(approach|option)\s+\d+/i.test(specificSection);

        if (isIntegrationSubsection) {
          // Integration subsection (### Approach 1) - add header back to improved content
          const header = subsectionHeaderRef.current || ''; // Read from ref (synchronous!)
          const improvedContentWithHeader = header ? `${header}\n${improvedText}` : improvedText;

          console.log('🔧 [MERGE] Integration Subsection Detected');
          console.log('🔧 [MERGE] Header from ref:', header);
          console.log('🔧 [MERGE] Improved text (first 300 chars):', improvedText.substring(0, 300));
          console.log('🔧 [MERGE] Merged content with header (first 300 chars):', improvedContentWithHeader.substring(0, 300));

          // Display in modal (just the body, header shown separately)
          setImprovedContent(improvedText);

          // The merging will be handled in handleUpdateImprovedContent in SplitViewIntegration
          // Store the full content WITH header for merging
          setCurrentSection(prev => ({
            ...prev,
            mergedContent: improvedContentWithHeader, // Full subsection with original header
            isSpecificSection: true,
            subsectionHeader: header // Also store in currentSection for modal display
          }));
        } else if ((currentSection?.type || section.type) === 'integration' && specificSection) {
          // Integration main section (## Documentation, ## Endpoints, etc.)
          console.log('🔧 [MERGE] Integration Main Section Detected:', specificSection);
          setImprovedContent(improvedText);

          // For main sections, just use the improved text as-is
          setCurrentSection(prev => ({
            ...prev,
            mergedContent: improvedText,
            isSpecificSection: true
          }));
        } else {
          // For modal display, show only the specific section with its header (OTHER TILES)
          const capitalizedSection = specificSection.split(' ').map((word, index) => {
            // Only capitalize first word and important words (not prepositions/articles)
            const lowercaseWords = ['to', 'of', 'in', 'for', 'on', 'at', 'by', 'a', 'an', 'the'];
            if (index === 0 || !lowercaseWords.includes(word.toLowerCase())) {
              return word.charAt(0).toUpperCase() + word.slice(1);
            }
            return word.toLowerCase();
          }).join(' ');
          const formattedSpecificSection = `**${capitalizedSection}:** ${improvedText}`;
          setImprovedContent(formattedSpecificSection);

          // For merging back into full content, we need the full text
          const sectionRegex = new RegExp(`(\\*\\*${specificSection}:?\\*\\*[\\s:]*)(?:[^\\*]|\\*(?!\\*)|\\n)+?(?=\\n\\*\\*|$)`, 'i');
          const mergedContent = contentToImprove.replace(sectionRegex, `$1 ${improvedText}`);

          // Store the merged content separately for when user clicks Update
          setCurrentSection(prev => ({
            ...prev,
            mergedContent: mergedContent,
            isSpecificSection: true
          }));
        }
      } else {
        // Full improvement - display as is
        setImprovedContent(improvedText);
        setCurrentSection(prev => ({
          ...prev,
          mergedContent: improvedText,
          isSpecificSection: false
        }));
      }
      
    } catch (error) {
      console.error('Error requesting improvement:', error);
      showNotification({
        message: 'Failed to generate improvement',
        type: 'error'
      });
      setShowImprovementModal(false);
    } finally {
      setIsImproving(false);
    }
  };

  const handleImproveClick = (section) => {
    // Set the section to show input
    setShowImproveInput(section.id);
    setImprovePrompt('');
    
    // Scroll to the section
    setTimeout(() => {
      const element = sectionRefs.current[section.id];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleSendImprove = (section) => {
    setShowImproveInput(null);
    requestImprovement(section, improvePrompt);
  };

  const handleCancelImprove = () => {
    setShowImproveInput(null);
    setImprovePrompt('');
  };

  const closeImprovementModal = () => {
    setShowImprovementModal(false);
    setCurrentSection(null);
    setImprovedContent('');
    setImprovePrompt('');
    setShowImproveInput(null); // Also hide the inline input
    setIsSpecificSectionImprovement(false);
    setSpecificSectionName(null);
    subsectionHeaderRef.current = null; // Reset subsection header ref
  };

  const registerSectionRef = (sectionId, ref) => {
    sectionRefs.current[sectionId] = ref;
  };

  return {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  };
};

export default useImprove;